<template>
  <div class="space-y-6">
    <!-- Header with Create Button -->
    <div class="flex items-center justify-between">
      <div>
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">ASAB Models</h3>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Augmented Service Access Bridge models with load balancing and fallback chains
        </p>
      </div>
      <Button variant="primary" @click="handleCreate">
        <svg
          class="w-4 h-4 mr-1.5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 4v16m8-8H4"
          />
        </svg>
        Create ASAB Model
      </Button>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <LoadingSpinner size="lg" />
    </div>

    <!-- Error State -->
    <Alert v-else-if="error" variant="error" class="mb-4">
      {{ error }}
    </Alert>

    <!-- Empty State -->
    <!-- eslint-disable-next-line asab-design-system/no-raw-button-card-markup -- dashed empty-state box, not a Card (no dashed/empty variant exists yet) -->
    <div
      v-else-if="asabModels.length === 0"
      class="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700"
    >
      <svg
        class="mx-auto h-12 w-12 text-gray-400"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01"
        />
      </svg>
      <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">No ASAB models</h3>
      <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Get started by creating your first ASAB model with advanced routing and load balancing.
      </p>
      <div class="mt-6">
        <Button variant="primary" @click="handleCreate">
          <svg
            class="w-4 h-4 mr-1.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 4v16m8-8H4"
            />
          </svg>
          Create ASAB Model
        </Button>
      </div>
    </div>

    <!-- Models Grid -->
    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <AsabModelCard
        v-for="model in asabModels"
        :key="model.slug"
        :model="model"
        @edit="handleEdit"
        @delete="handleDelete"
      />
    </div>

    <!-- ASAB Model Builder Modal -->
    <AsabModelBuilder
      :open="isBuilderOpen"
      :edit-model="editingModel"
      :available-base-models="availableBaseModels"
      :available-custom-models="availableCustomModels"
      @update:open="isBuilderOpen = $event"
      @created="handleCreated"
      @updated="handleUpdated"
      @cancel="handleBuilderCancel"
    />

    <!-- Confirm Delete Modal -->
    <ConfirmDeleteModal
      :open="isDeleteModalOpen"
      :model="deletingModel"
      model-type="asab"
      @update:open="isDeleteModalOpen = $event"
      @confirm="handleDeleteConfirm"
      @cancel="handleDeleteCancel"
    />
  </div>
</template>

<script setup lang="ts">
  import { ref, onMounted, computed } from 'vue';
  import { Button, LoadingSpinner, Alert } from '@asabdev/asab-ui';
  import { useNotification } from '@asabdev/asab-ui';
  import AsabModelCard from './AsabModelCard.vue';
  import AsabModelBuilder from './AsabModelBuilder.vue';
  import ConfirmDeleteModal from './ConfirmDeleteModal.vue';
  import { useAsabModels } from '../composables/useAsabModels';
  import { useBaseModels } from '../composables/useBaseModels';
  import { useCustomModels } from '../composables/useCustomModels';
  import type {
    AsabModelResponse,
    CreateAsabModelRequest,
    UpdateAsabModelRequest,
  } from '../types';

  interface Emits {
    (e: 'edit', model: AsabModelResponse): void;
    (e: 'delete', model: AsabModelResponse): void;
    (e: 'create'): void;
  }

  const emit = defineEmits<Emits>();

  // Composables
  const {
    asabModels,
    isLoading,
    error,
    fetchAsabModels,
    createAsabModel,
    updateAsabModel,
    deleteAsabModel,
  } = useAsabModels();

  const { baseModels, fetchBaseModels } = useBaseModels();
  const { customModels, fetchCustomModels } = useCustomModels();

  const { showSuccess, showError } = useNotification();

  // Local state
  const isBuilderOpen = ref(false);
  const isDeleteModalOpen = ref(false);
  const editingModel = ref<AsabModelResponse | null>(null);
  const deletingModel = ref<AsabModelResponse | null>(null);

  // Computed properties for available models
  const availableBaseModels = computed(() => baseModels.value);
  const availableCustomModels = computed(() => customModels.value);

  // Methods
  const handleCreate = () => {
    editingModel.value = null;
    isBuilderOpen.value = true;
    emit('create');
  };

  const handleEdit = (model: AsabModelResponse) => {
    editingModel.value = model;
    isBuilderOpen.value = true;
    emit('edit', model);
  };

  const handleDelete = (model: AsabModelResponse) => {
    deletingModel.value = model;
    isDeleteModalOpen.value = true;
    emit('delete', model);
  };

  const handleCreated = async (modelData: Partial<AsabModelResponse>) => {
    try {
      // Build the create request
      const createRequest: CreateAsabModelRequest = {
        name: modelData.name!,
        slug: modelData.slug!,
        routing_targets: (modelData as Record<string, unknown>).routing_targets as CreateAsabModelRequest['routing_targets'],
        fallback_chain: (modelData as Record<string, unknown>).fallback_chain as CreateAsabModelRequest['fallback_chain'],
        limits: (modelData as Record<string, unknown>).limits as CreateAsabModelRequest['limits'],
      };

      const result = await createAsabModel(createRequest);

      if (result) {
        showSuccess(`Successfully created ASAB model "${result.name}"`);
        isBuilderOpen.value = false;
        editingModel.value = null;
        // Refresh the list
        await fetchAsabModels();
      } else {
        showError('Failed to create ASAB model. Please try again.');
      }
    } catch (err) {
      showError('An error occurred while creating the ASAB model.');
      console.error('Error creating ASAB model:', err);
    }
  };

  const handleUpdated = async (modelData: Partial<AsabModelResponse>) => {
    if (!editingModel.value) return;

    try {
      // Build the update request
      const updateRequest: UpdateAsabModelRequest = {
        name: modelData.name,
        routing_targets: (modelData as Record<string, unknown>).routing_targets as UpdateAsabModelRequest['routing_targets'],
        fallback_chain: (modelData as Record<string, unknown>).fallback_chain as UpdateAsabModelRequest['fallback_chain'],
        limits: (modelData as Record<string, unknown>).limits as UpdateAsabModelRequest['limits'],
      };

      const result = await updateAsabModel(editingModel.value.slug, updateRequest);

      if (result) {
        showSuccess(`Successfully updated ASAB model "${result.name}"`);
        isBuilderOpen.value = false;
        editingModel.value = null;
        // Refresh the list
        await fetchAsabModels();
      } else {
        showError('Failed to update ASAB model. Please try again.');
      }
    } catch (err) {
      showError('An error occurred while updating the ASAB model.');
      console.error('Error updating ASAB model:', err);
    }
  };

  const handleBuilderCancel = () => {
    isBuilderOpen.value = false;
    editingModel.value = null;
  };

  const handleDeleteConfirm = async () => {
    if (!deletingModel.value) return;

    try {
      const success = await deleteAsabModel(deletingModel.value.slug);

      if (success) {
        showSuccess(`Successfully deleted ASAB model "${deletingModel.value.name}"`);
        isDeleteModalOpen.value = false;
        deletingModel.value = null;
        // Refresh the list
        await fetchAsabModels();
      } else {
        showError('Failed to delete ASAB model. Please try again.');
      }
    } catch (err) {
      showError('An error occurred while deleting the ASAB model.');
      console.error('Error deleting ASAB model:', err);
    }
  };

  const handleDeleteCancel = () => {
    isDeleteModalOpen.value = false;
    deletingModel.value = null;
  };

  // Lifecycle
  onMounted(async () => {
    // Fetch all required data
    await Promise.all([
      fetchAsabModels(),
      fetchBaseModels(),
      fetchCustomModels(),
    ]);
  });
</script>
